import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Plus, Trash2, Play, Pause, ChevronRight,
  Zap, Calendar, Target, BarChart3, FileText, Settings2,
  X, Save, Clock, ArrowLeft, Sparkles, Eye, EyeOff, Pencil,
  Globe, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Mon' }, { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' }, { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' }, { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' },
];

/* ─── helpers ─── */
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function totalPlannedArticles(blocks) {
  let total = 0;
  const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  for (const b of blocks) {
    if (!b.start_date || !b.end_date || !b.days?.length) continue;
    const end = new Date(b.end_date);
    let d = new Date(b.start_date);
    while (d <= end) {
      const dn = Object.entries(dayMap).find(([, v]) => v === d.getDay())?.[0];
      if (dn && b.days.map(x => x.toLowerCase()).includes(dn)) total++;
      d.setDate(d.getDate() + 1);
    }
  }
  return total;
}

/* ─── AI Enhance Button ─── */
const AiEnhanceButton = ({ text, field, headers, onResult }) => {
  const [loading, setLoading] = useState(false);
  const enhance = async () => {
    if (!text?.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/campaigns/ai/enhance`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text, field }),
      });
      if (res.ok) { const d = await res.json(); onResult(d.enhanced); }
      else { alert('AI enhancement failed'); }
    } catch { alert('AI enhancement failed'); }
    setLoading(false);
  };
  return (
    <button onClick={enhance} disabled={loading || !text?.trim()} type="button"
      className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 disabled:opacity-40 transition-colors" data-testid={`ai-enhance-${field}`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {loading ? 'Enhancing...' : 'Enhance with AI'}
    </button>
  );
};

/* ─── Cadence Block ─── */
const CadenceBlockRow = ({ block, index, onChange, onRemove }) => {
  const update = (field, val) => onChange(index, { ...block, [field]: val });
  const toggleDay = (dayId) => {
    const days = block.days || [];
    update('days', days.includes(dayId) ? days.filter(d => d !== dayId) : [...days, dayId]);
  };
  return (
    <div className="flex flex-col gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`cadence-block-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Block {index + 1}</span>
        <button onClick={() => onRemove(index)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-xs text-slate-500">Start</label><input type="date" value={block.start_date || ''} onChange={e => update('start_date', e.target.value)} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-taxera-blue outline-none" /></div>
        <div><label className="text-xs text-slate-500">End</label><input type="date" value={block.end_date || ''} onChange={e => update('end_date', e.target.value)} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-taxera-blue outline-none" /></div>
      </div>
      <div><label className="text-xs text-slate-500 mb-1 block">Days</label>
        <div className="flex gap-1 flex-wrap">
          {DAYS_OF_WEEK.map(d => (
            <button key={d.id} onClick={() => toggleDay(d.id)} className={`text-xs px-2 py-1 rounded-full border transition-colors ${(block.days || []).includes(d.id) ? 'bg-taxera-blue text-white border-taxera-blue' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>{d.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Angle Editor ─── */
const AngleEditor = ({ angles, onChange }) => {
  const totalWeight = angles.reduce((s, a) => s + (a.weight || 0), 0);
  const updateAngle = (idx, field, val) => { const u = [...angles]; u[idx] = { ...u[idx], [field]: val }; onChange(u); };
  const removeAngle = (idx) => onChange(angles.filter((_, i) => i !== idx));
  const addAngle = () => onChange([...angles, { id: `custom-${Date.now()}`, name: '', weight: 10 }]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Content angles</span>
        <span className={`text-xs font-mono ${totalWeight === 100 ? 'text-green-600' : 'text-amber-600'}`}>Total: {totalWeight}%</span>
      </div>
      {angles.map((a, i) => (
        <div key={a.id} className="flex items-center gap-2" data-testid={`angle-row-${i}`}>
          <input value={a.name} onChange={e => updateAngle(i, 'name', e.target.value)} placeholder="Angle name" className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-taxera-blue outline-none" />
          <div className="flex items-center gap-1 w-20">
            <input type="number" min={0} max={100} value={a.weight} onChange={e => updateAngle(i, 'weight', parseInt(e.target.value) || 0)} className="w-14 text-sm text-center border border-slate-200 rounded px-1 py-1.5 focus:ring-1 focus:ring-taxera-blue outline-none" />
            <span className="text-xs text-slate-400">%</span>
          </div>
          <button onClick={() => removeAngle(i)} className="text-red-400 hover:text-red-600 p-1"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={addAngle} className="flex items-center gap-1 text-xs text-taxera-blue hover:underline mt-1" data-testid="add-angle-btn"><Plus className="w-3 h-3" /> Add custom angle</button>
    </div>
  );
};

/* ─── Campaign Form ─── */
const CampaignForm = ({ initial, defaultAngles, onSave, onCancel, saving, headers }) => {
  const [form, setForm] = useState(() => ({
    name: '', briefing: '', tone_notes: '', mandate_deadline: '', start_date: '', end_date: '',
    cadence_blocks: [{ start_date: '', end_date: '', days: ['tuesday', 'saturday'] }],
    angles: defaultAngles || [], active: true, priority: 1, country_codes: [], owner: '', public_summary: '',
    ...(initial || {}),
  }));
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const updateBlock = (idx, block) => { const b = [...form.cadence_blocks]; b[idx] = block; set('cadence_blocks', b); };
  const removeBlock = (idx) => set('cadence_blocks', form.cadence_blocks.filter((_, i) => i !== idx));
  const addBlock = () => set('cadence_blocks', [...form.cadence_blocks, { start_date: '', end_date: '', days: [] }]);
  const planned = totalPlannedArticles(form.cadence_blocks);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Name */}
      <div>
        <label className="text-sm font-medium text-slate-700">Campaign name *</label>
        <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Spain VeriFactu 2027"
          className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none" data-testid="campaign-name-input" />
      </div>

      {/* Owner */}
      <div>
        <label className="text-sm font-medium text-slate-700">Campaign owner</label>
        <input value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="e.g. Paul Antunes"
          className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none" data-testid="campaign-owner-input" />
      </div>

      {/* Briefing + AI */}
      <div>
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Campaign briefing *</label>
          <AiEnhanceButton text={form.briefing} field="briefing" headers={headers} onResult={v => set('briefing', v)} />
        </div>
        <textarea value={form.briefing} onChange={e => set('briefing', e.target.value)} rows={5}
          placeholder="Describe the mandate, target audience, positioning, key deadlines... The AI assistant can help expand your draft."
          className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none resize-y" data-testid="campaign-briefing-input" />
      </div>

      {/* Tone + AI (moved up directly below briefing) */}
      <div>
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Tone & voice notes <span className="text-slate-400 font-normal">(optional)</span></label>
          <AiEnhanceButton text={form.tone_notes} field="tone" headers={headers} onResult={v => set('tone_notes', v)} />
        </div>
        <textarea value={form.tone_notes} onChange={e => set('tone_notes', e.target.value)} rows={2}
          placeholder="e.g. More urgent tone, address mid-market CFOs specifically"
          className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none resize-y" data-testid="campaign-tone-notes" />
      </div>

      {/* Public summary — what visitors see on /mandates */}
      <div>
        <label className="text-sm font-medium text-slate-700">Public summary <span className="text-slate-400 font-normal">(shown on the website)</span></label>
        <textarea value={form.public_summary} onChange={e => set('public_summary', e.target.value)} rows={2}
          placeholder="A short, visitor-facing description of this mandate. Unlike the briefing above, this text is shown publicly on the Mandates page."
          className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none resize-y" data-testid="campaign-public-summary" />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-3 gap-3">
        <div><label className="text-sm font-medium text-slate-700">Start date</label><input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none" data-testid="campaign-start-date" /></div>
        <div><label className="text-sm font-medium text-slate-700">End date</label><input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none" data-testid="campaign-end-date" /></div>
        <div><label className="text-sm font-medium text-slate-700">Mandate deadline</label><input type="date" value={form.mandate_deadline} onChange={e => set('mandate_deadline', e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none" data-testid="campaign-deadline" /></div>
      </div>

      {/* Priority */}
      <div>
        <label className="text-sm font-medium text-slate-700">Priority <span className="text-slate-400 font-normal">(lower = higher priority)</span></label>
        <input type="number" min={1} max={10} value={form.priority} onChange={e => set('priority', parseInt(e.target.value) || 1)} className="mt-1 w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-taxera-blue outline-none" data-testid="campaign-priority" />
      </div>

      {/* Cadence */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700">Cadence schedule</label>
          {planned > 0 && <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">~{planned} articles planned</span>}
        </div>
        <div className="space-y-2">
          {form.cadence_blocks.map((block, i) => <CadenceBlockRow key={i} block={block} index={i} onChange={updateBlock} onRemove={removeBlock} />)}
        </div>
        <button onClick={addBlock} className="flex items-center gap-1 text-xs text-taxera-blue hover:underline mt-2" data-testid="add-cadence-block"><Plus className="w-3 h-3" /> Add cadence block</button>
      </div>

      {/* Angles */}
      <AngleEditor angles={form.angles} onChange={a => set('angles', a)} />

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={() => onSave(form)} disabled={saving || !form.name.trim() || !form.briefing.trim()}
          className="flex items-center gap-2 bg-taxera-blue text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-taxera-blue/90 disabled:opacity-40 transition-colors" data-testid="save-campaign-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {initial ? 'Save changes' : 'Create campaign'}
        </button>
        <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-700" data-testid="cancel-campaign-btn">Cancel</button>
      </div>
    </div>
  );
};

/* ─── Article Preview Modal ─── */
const ArticlePreview = ({ article, campaignId, headers, onClose, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: article.title, excerpt: article.excerpt, content: article.content });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [isPublished, setIsPublished] = useState(article.published);

  const handlePublishToggle = async () => {
    setToggling(true);
    try {
      const res = await fetch(`${API}/api/campaigns/${campaignId}/articles/${article.slug}/publish`, { method: 'PUT', headers, credentials: 'include' });
      if (res.ok) { const d = await res.json(); setIsPublished(d.published); }
    } catch {}
    setToggling(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${API}/api/campaigns/${campaignId}/articles/${article.slug}`, {
      method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(editForm),
    });
    setSaving(false);
    setEditing(false);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mb-10" onClick={e => e.stopPropagation()} data-testid="article-preview-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isPublished ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
              {isPublished ? 'Published' : 'Draft'}
            </span>
            {article.angle_name && <span className="text-xs bg-taxera-blue/10 text-taxera-blue px-2 py-0.5 rounded">{article.angle_name}</span>}
            {article.web_context_used && <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded flex items-center gap-1"><Globe className="w-3 h-3" /> Web-grounded</span>}
            {article.country_codes?.length > 0 && article.country_codes.map(cc => (
              <img key={cc} src={`https://flagcdn.com/20x15/${cc.toLowerCase()}.png`} alt={cc} className="inline-block" title={cc} />
            ))}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {editing ? (
            <>
              <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="w-full text-xl font-bold border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-taxera-blue outline-none" />
              <textarea value={editForm.excerpt} onChange={e => setEditForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-taxera-blue outline-none" placeholder="Excerpt..." />
              <textarea value={editForm.content} onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))} rows={18} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-taxera-blue outline-none font-mono" />
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900">{article.title}</h2>
              <p className="text-sm text-slate-500 italic">{article.excerpt}</p>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{article.author?.name}</span>
                <span>{article.reading_time} min read</span>
                <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
              </div>
              {article.tags?.length > 0 && (
                <div className="flex gap-1 flex-wrap">{article.tags.map(t => <span key={t} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{t}</span>)}</div>
              )}
              {article.hashtags?.length > 0 && (
                <div className="flex gap-1 flex-wrap">{article.hashtags.map(h => <span key={h} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-medium">{h}</span>)}</div>
              )}
              <div className="prose prose-sm max-w-none text-slate-700 border-t border-slate-100 pt-4" dangerouslySetInnerHTML={{ __html: (article.content || '').replace(/^## (.*)/gm, '<h2 class="text-lg font-bold mt-6 mb-2 text-slate-800">$1</h2>').replace(/^### (.*)/gm, '<h3 class="text-base font-semibold mt-4 mb-1 text-slate-700">$1</h3>').replace(/\n- (.*)/g, '<li class="ml-4">$1</li>').replace(/\n\n/g, '<br/><br/>') }} />
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
          <div className="flex items-center gap-2">
            <button onClick={handlePublishToggle} disabled={toggling}
              className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg font-medium transition-colors ${isPublished ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100' : 'bg-green-600 text-white hover:bg-green-700'}`} data-testid="toggle-publish-btn">
              {toggling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isPublished ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {isPublished ? 'Unpublish' : 'Publish to Blog'}
            </button>
            {isPublished && article.slug && (
              <a href={`/blog/${article.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-taxera-blue hover:underline">
                <ExternalLink className="w-3 h-3" /> View on site
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 text-sm bg-taxera-blue text-white px-4 py-2 rounded-lg font-medium hover:bg-taxera-blue/90 disabled:opacity-40">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
                <button onClick={() => setEditing(false)} className="text-sm text-slate-500 hover:text-slate-700">Cancel</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid="edit-article-btn">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Campaign Detail ─── */
const CampaignDetail = ({ campaign, headers, onBack, onRefresh }) => {
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [defaultAngles, setDefaultAngles] = useState([]);

  useEffect(() => { fetch(`${API}/api/campaigns/meta/default-angles`).then(r => r.json()).then(d => setDefaultAngles(d.angles || [])).catch(() => {}); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/campaigns/${campaign.id}/generate`, { method: 'POST', headers, credentials: 'include' });
      if (!res.ok) { const err = await res.json(); alert(err.detail || 'Generation failed'); }
      else { onRefresh(); }
    } catch (e) { alert('Generation failed: ' + e.message); }
    setGenerating(false);
  };

  const handleToggle = async () => { await fetch(`${API}/api/campaigns/${campaign.id}/toggle`, { method: 'PUT', headers, credentials: 'include' }); onRefresh(); };

  const handleSave = async (form) => {
    setSaving(true);
    await fetch(`${API}/api/campaigns/${campaign.id}`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    setSaving(false); setEditing(false); onRefresh();
  };

  const deadline = daysUntil(campaign.mandate_deadline);
  const planned = totalPlannedArticles(campaign.cadence_blocks || []);
  const articles = campaign.articles || [];
  const angleCoverage = campaign.angle_coverage || {};
  const angles = campaign.angles || [];
  const published = articles.filter(a => a.published).length;
  const drafts = articles.filter(a => !a.published).length;

  if (editing) return (
    <div>
      <button onClick={() => setEditing(false)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <CampaignForm initial={campaign} defaultAngles={defaultAngles} onSave={handleSave} onCancel={() => setEditing(false)} saving={saving} headers={headers} />
    </div>
  );

  return (
    <div className="space-y-5">
      {previewArticle && <ArticlePreview article={previewArticle} campaignId={campaign.id} headers={headers} onClose={() => setPreviewArticle(null)} onRefresh={() => { setPreviewArticle(null); onRefresh(); }} />}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2" data-testid="campaign-back-btn"><ArrowLeft className="w-4 h-4" /> All campaigns</button>
          <h2 className="text-xl font-bold text-slate-800">{campaign.name}</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl line-clamp-3">{campaign.briefing}</p>
          {campaign.slug && campaign.slug !== 'null' && <a href={`/mandates/${campaign.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-taxera-blue hover:underline mt-1"><Globe className="w-3 h-3" /> View mandate page</a>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50" data-testid="edit-campaign-btn"><Settings2 className="w-3.5 h-3.5" /> Edit</button>
          <button onClick={handleToggle} className={`flex items-center gap-1 text-sm rounded-lg px-3 py-1.5 ${campaign.active ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-testid="toggle-campaign-btn">
            {campaign.active ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Activate</>}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-6 gap-3">
        {[
          { val: articles.length, label: 'Total articles', color: 'text-slate-800' },
          { val: published, label: 'Published', color: 'text-green-600' },
          { val: drafts, label: 'Drafts', color: 'text-amber-600' },
          { val: planned, label: 'Planned total', color: 'text-slate-800' },
          { val: deadline !== null ? `${deadline}d` : '-', label: 'Until deadline', color: deadline !== null && deadline < 90 ? 'text-red-600' : 'text-slate-800' },
          { val: campaign.owner || '-', label: 'Owner', color: 'text-slate-800', isText: true },
        ].map((s, i) => (
          <div key={i} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
            <div className={`${s.isText ? 'text-sm truncate' : 'text-2xl'} font-bold ${s.color}`}>{s.val}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Generate */}
      <button onClick={handleGenerate} disabled={generating} className="flex items-center gap-2 bg-taxera-charcoal text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-taxera-charcoal/90 disabled:opacity-40 transition-colors" data-testid="generate-now-btn">
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        {generating ? 'Generating article...' : 'Generate article now'}
      </button>

      {/* Angle coverage */}
      {angles.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><Target className="w-4 h-4" /> Angle coverage</h3>
          <div className="grid grid-cols-2 gap-1.5">
            {angles.map(a => {
              const count = angleCoverage[a.id] || 0;
              const maxCount = Math.max(...Object.values(angleCoverage).map(Number), 1);
              return (
                <div key={a.id} className="flex items-center gap-2 text-xs">
                  <span className="w-36 truncate text-slate-600">{a.name}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden"><div className="bg-taxera-blue h-full rounded-full transition-all" style={{ width: `${Math.round((count / maxCount) * 100) || 2}%` }} /></div>
                  <span className="text-slate-500 w-6 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Articles — clickable with drill-down */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><FileText className="w-4 h-4" /> Articles ({articles.length})</h3>
        {articles.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No articles generated yet. Click "Generate article now" to create the first one.</p>
        ) : (
          <div className="space-y-1.5">
            {articles.map(a => (
              <div key={a.id} onClick={() => setPreviewArticle(a)} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-slate-200 hover:border-taxera-blue/40 hover:shadow-sm cursor-pointer transition-all group" data-testid={`campaign-article-${a.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 group-hover:text-taxera-blue transition-colors">{a.title}</div>
                  {a.excerpt && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{a.excerpt}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    {a.angle_name && <span className="text-2xs bg-taxera-blue/10 text-taxera-blue px-1.5 py-0.5 rounded">{a.angle_name}</span>}
                    <span className={`text-2xs px-1.5 py-0.5 rounded ${a.published ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>{a.published ? 'Published' : 'Draft'}</span>
                    {a.web_context_used && <span className="text-2xs text-violet-500">Web-grounded</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <span className="text-xs text-slate-400 whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-taxera-blue transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Campaign Card ─── */
const CampaignCard = ({ campaign, onClick, onDelete }) => {
  const deadline = daysUntil(campaign.mandate_deadline);
  const planned = totalPlannedArticles(campaign.cadence_blocks || []);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete campaign "${campaign.name}"? Generated articles will be kept.`)) return;
    setDeleting(true); await onDelete(campaign.id); setDeleting(false);
  };
  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-taxera-blue/40 hover:shadow-sm transition-all cursor-pointer group" data-testid={`campaign-card-${campaign.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${campaign.active ? 'bg-green-500' : 'bg-slate-300'}`} />
          <h3 className="font-semibold text-slate-800 text-sm">{campaign.name}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleDelete} disabled={deleting} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-1 transition-opacity" data-testid={`delete-campaign-${campaign.id}`}>
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-taxera-blue transition-colors" />
        </div>
      </div>
      <p className="text-xs text-slate-500 line-clamp-2 mb-3">{campaign.briefing}</p>
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {campaign.article_count || 0} articles</span>
        {planned > 0 && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> ~{planned} planned</span>}
        {deadline !== null && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {deadline}d to deadline</span>}
      </div>
    </div>
  );
};

/* ─── Main Tab ─── */
const CampaignsTab = ({ headers }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultAngles, setDefaultAngles] = useState([]);

  const fetchCampaigns = useCallback(async () => {
    try { const res = await fetch(`${API}/api/campaigns`, { headers, credentials: 'include' }); if (res.ok) setCampaigns((await res.json()).campaigns || []); } catch (e) { console.error(e); } setLoading(false);
  }, [headers]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try { const res = await fetch(`${API}/api/campaigns/${id}`, { headers, credentials: 'include' }); if (res.ok) setSelectedCampaign(await res.json()); } catch (e) { console.error(e); }
    setDetailLoading(false);
  }, [headers]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => { fetch(`${API}/api/campaigns/meta/default-angles`).then(r => r.json()).then(d => setDefaultAngles(d.angles || [])).catch(() => {}); }, []);

  const openDetail = (id) => { setSelectedId(id); setView('detail'); fetchDetail(id); };
  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/campaigns`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
      if (res.ok) { const d = await res.json(); openDetail(d.id); fetchCampaigns(); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };
  const handleDelete = async (id) => { await fetch(`${API}/api/campaigns/${id}`, { method: 'DELETE', headers, credentials: 'include' }); fetchCampaigns(); if (selectedId === id) setView('list'); };
  const refreshDetail = () => { if (selectedId) fetchDetail(selectedId); fetchCampaigns(); };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;

  if (view === 'create') return (
    <div>
      <button onClick={() => setView('list')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" /> All campaigns</button>
      <h2 className="text-lg font-bold text-slate-800 mb-4">Create campaign</h2>
      <CampaignForm defaultAngles={defaultAngles} onSave={handleCreate} onCancel={() => setView('list')} saving={saving} headers={headers} />
    </div>
  );

  if (view === 'detail') {
    if (detailLoading || !selectedCampaign) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;
    return <CampaignDetail campaign={selectedCampaign} headers={headers} onBack={() => { setView('list'); setSelectedId(null); }} onRefresh={refreshDetail} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Content campaigns</h2>
          <p className="text-sm text-slate-500">Create focused content campaigns around key tax mandates and topics</p>
        </div>
        <button onClick={() => setView('create')} className="flex items-center gap-2 bg-taxera-blue text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-taxera-blue/90 transition-colors" data-testid="create-campaign-btn"><Plus className="w-4 h-4" /> New campaign</button>
      </div>
      {campaigns.length === 0 ? (
        <div className="text-center py-16">
          <Target className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-slate-600 mb-1">No campaigns yet</h3>
          <p className="text-xs text-slate-400 mb-4">Create your first content campaign to start generating focused articles</p>
          <button onClick={() => setView('create')} className="text-sm text-taxera-blue hover:underline" data-testid="create-first-campaign-btn">Create campaign</button>
        </div>
      ) : (
        <div className="grid gap-3">{campaigns.map(c => <CampaignCard key={c.id} campaign={c} onClick={() => openDetail(c.id)} onDelete={handleDelete} />)}</div>
      )}
    </div>
  );
};

export default CampaignsTab;
